import { ChangeEventHandler, useEffect, useRef, useState, type FormEvent } from 'react';
import styled from '@emotion/styled';
import { useAtomValue } from 'jotai/react';
import { useTranslation } from 'next-i18next';
import { DTOUserLanguageEntry } from 'src/apis/dto/DTOUserLanguageEntry';
import LocaleSelect from 'src/components/LocalePreference/Select';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import { useRestQuery } from 'src/hooks/useRestQuery';
import useScrollBox from 'src/hooks/useScrollBox';
import { tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { isRegisterLoadingAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import type { SignUpSettingsResponse } from 'src/types/register';
import { pushEvent } from 'src/utils/event-gateway';
import { Container as ContainerBase, LoadingIndicator, NextButton, NoticeText, Section, Title } from './styles';
import type { StepProps } from './types';
const Container = styled(ContainerBase)`
  ${({
  theme
}) => theme.screenSize.desktop} {
    max-height: 570px;
  }
`;
const InputSection = styled(Section)<{
  scrollPosition: ReturnType<typeof useScrollBox>['scrollPosition'];
}>`
  position: relative;

  flex: 1;
  min-height: 0;

  ${({
  scrollPosition
}) => {
  switch (scrollPosition) {
    case 'TOP':
      return `mask-image: linear-gradient(to bottom, black 0%, black calc(100% - 40px), transparent 100%);`;
    case 'BOTTOM':
      return `mask-image: linear-gradient(to bottom, transparent 0%, black 40px, black 100%);`;
    case 'SCROLLING':
      return `mask-image: linear-gradient(to bottom, transparent 0%, black 40px, black calc(100% - 40px), transparent 100%);`;
    default:
      return ``;
  }
}}
`;
const Sentinel = styled.div`
  width: 100%;
  height: 1px;
  margin-top: -1px;
`;
const InputContainer = styled(Section)`
  position: relative;

  flex: 1;
  min-height: 0;

  overflow-y: auto;
  ${({
  theme
}) => theme.scroll}
`;
const StyledLocaleSelect = styled(LocaleSelect)`
  color: ${({
  theme
}) => theme.color.content12};
`;
const KEY = 'language';
export default function LanguageStep({
  onNext,
  className
}: StepProps<DTOUserLanguageEntry[]>) {
  const {
    t
  } = useTranslation();
  const isRegisterLoading = useAtomValue(isRegisterLoadingAtom);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock(containerRef, true);
  const {
    topSentinel,
    bottomSentinel,
    scrollPosition
  } = useScrollBox({
    rootMargin: '8px'
  });
  const {
    isLoading,
    data: {
      data: signupSettings
    } = {}
  } = useRestQuery<void, SignUpSettingsResponse, void>({
    path: '/api/v1/signup-settings',
    withoutAuth: true
  });
  const tryLoginType = useAtomValue(tryLoginTypeAtom);
  const [languages, setLanguages] = useState<string[]>([]);
  const isButtonDisabled = isLoading || isRegisterLoading || languages.length === 0;
  const getIsDisabled = (code: string) => signupSettings?.preferredLanguageSettings?.maxPreferredLanguageCount != null && languages.length >= signupSettings.preferredLanguageSettings?.maxPreferredLanguageCount && !languages.includes(code);
  const handleToggle = (code: string): ChangeEventHandler<HTMLInputElement> => {
    return event => {
      const {
        checked
      } = event.currentTarget;
      setLanguages(prev => checked ? [...prev, code] : prev.filter(lang => lang !== code));
    };
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const preferredLanguages = signupSettings?.preferredLanguageSettings?.preferredLanguages.filter(({
      code
    }) => languages.includes(code)) ?? [];
    try {
      await onNext(preferredLanguages);
    } catch {
      // noop
    } finally {
      pushEvent({
        eventType: EVENT_TYPE.SIGN_UP,
        eventName: EVENT_NAME.SIGNUP__CLICK_NEXT_PREFERRED_LANGUAGE,
        eventParams: {
          selected_values: preferredLanguages.map(({
            code
          }) => code).join(',')
        }
      });
    }
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__IMPRESSION_PREFERRED_LANGUAGE,
      eventParams: {
        loginType: tryLoginType
      }
    });
  }, [tryLoginType]);
  return <Container as='form' onSubmit={handleSubmit} className={className} data-sentry-element="Container" data-sentry-component="LanguageStep" data-sentry-source-file="Language.tsx">
      <Section data-sentry-element="Section" data-sentry-source-file="Language.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="Language.tsx">{t('vividi_onboarding_select_language_title')}</Title>
        <NoticeText data-sentry-element="NoticeText" data-sentry-source-file="Language.tsx">{t('vividi_onboarding_select_language_desc')}</NoticeText>
      </Section>
      <InputSection scrollPosition={scrollPosition} data-sentry-element="InputSection" data-sentry-source-file="Language.tsx">
        <InputContainer ref={containerRef} data-sentry-element="InputContainer" data-sentry-source-file="Language.tsx">
          <Sentinel ref={topSentinel} data-sentry-element="Sentinel" data-sentry-source-file="Language.tsx" />
          {isLoading || signupSettings == null ? null : signupSettings?.preferredLanguageSettings?.preferredLanguages?.map(({
          code,
          displayName
        }) => <StyledLocaleSelect key={code} name={KEY} value={code} onChange={handleToggle(code)} label={displayName} disabled={getIsDisabled(code)} />)}
          <Sentinel ref={bottomSentinel} data-sentry-element="Sentinel" data-sentry-source-file="Language.tsx" />
        </InputContainer>
      </InputSection>
      <Section data-sentry-element="Section" data-sentry-source-file="Language.tsx">
        <NextButton type='submit' disabled={isButtonDisabled} data-sentry-element="NextButton" data-sentry-source-file="Language.tsx">
          {isRegisterLoading ? <LoadingIndicator /> : t('SIGNUP__NEXT')}
        </NextButton>
      </Section>
    </Container>;
}